import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Check, ExternalLink, TrendingDown } from 'lucide-react';
import { GPU, performanceTiers, gpuData, sortGPUsByPerformance, getTierForPerformance } from '@/data/gpuData';
import { getLowestPrice } from '@/data/vendorPrices';
import { FilterBar, GPUFilters } from '@/components/FilterBar';
import { CompareBar, CompareModal } from '@/components/CompareModal';
import { cn } from '@/lib/utils';

interface GPUCardProps {
  gpu: GPU;
  isSelected: boolean;
  onToggleSelect: (gpu: GPU) => void;
  canSelect: boolean;
}

const GPUCard = ({ gpu, isSelected, onToggleSelect, canSelect }: GPUCardProps) => {
  const lowestPrice = getLowestPrice(gpu.id);
  
  const brandColors = {
    nvidia: 'bg-nvidia/10 border-nvidia/30 hover:border-nvidia/60 hover:shadow-glow-nvidia',
    amd: 'bg-amd/10 border-amd/30 hover:border-amd/60 hover:shadow-glow-amd',
    intel: 'bg-intel/10 border-intel/30 hover:border-intel/60',
  };

  const brandTextColors = {
    nvidia: 'text-nvidia',
    amd: 'text-amd',
    intel: 'text-intel',
  };

  const selectedBorderColors = {
    nvidia: 'border-nvidia ring-2 ring-nvidia/30',
    amd: 'border-amd ring-2 ring-amd/30',
    intel: 'border-intel ring-2 ring-intel/30',
  };

  return (
    <div
      className={cn(
        'p-3 rounded-lg border transition-all duration-300 group relative',
        brandColors[gpu.brand],
        isSelected && selectedBorderColors[gpu.brand]
      )}
    >
      {/* Selection indicator */}
      <button
        className={cn(
          'absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all z-10',
          isSelected
            ? `${brandTextColors[gpu.brand]} border-current bg-current`
            : 'border-muted-foreground/30 hover:border-muted-foreground/60',
          !canSelect && !isSelected && 'opacity-30 cursor-not-allowed'
        )}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (canSelect || isSelected) onToggleSelect(gpu);
        }}
        disabled={!canSelect && !isSelected}
        aria-label={isSelected ? `Remove ${gpu.name} from comparison` : `Add ${gpu.name} to comparison`}
      >
        {isSelected ? (
          <Check className="w-3.5 h-3.5 text-white" />
        ) : (
          <Plus className="w-3.5 h-3.5 text-muted-foreground" />
        )}
      </button>

      <Link to={`/gpu/${gpu.id}`} className="block cursor-pointer">
        <div className="flex items-center justify-between gap-2 mb-2 pr-8">
          <span className={cn('text-xs font-semibold', brandTextColors[gpu.brand])}>
            {gpu.performance}%
          </span>
          <span className="text-xs text-muted-foreground">{gpu.vram}</span>
        </div>
        <h4 className="font-semibold text-sm text-foreground group-hover:text-foreground/90 transition-colors flex items-center gap-1">
          {gpu.name}
          <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
        </h4>
        <div className="flex items-center justify-between mt-2 text-xs">
          {lowestPrice ? (
            <div className="flex items-center gap-1">
              <span className="font-semibold text-green-600">${lowestPrice.price}</span>
              {lowestPrice.savingsPercent > 0 && (
                <span className="text-green-600 flex items-center">
                  <TrendingDown className="w-3 h-3" />
                  <span className="text-[10px]">{lowestPrice.savingsPercent}%</span>
                </span>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">${gpu.msrp}</span>
          )}
          <span className="text-muted-foreground">{gpu.tdp}W</span>
        </div>
      </Link>
    </div>
  );
};

const TierDivider = ({ tier }: { tier: typeof performanceTiers[0] }) => {
  const tierColors = {
    ultra: 'from-tier-ultra/20 via-tier-ultra/5 to-transparent border-tier-ultra/40',
    high: 'from-tier-high/20 via-tier-high/5 to-transparent border-tier-high/40',
    mid: 'from-tier-mid/20 via-tier-mid/5 to-transparent border-tier-mid/40',
    entry: 'from-tier-entry/20 via-tier-entry/5 to-transparent border-tier-entry/40',
  };

  const tierBadgeColors = {
    ultra: 'bg-tier-ultra text-white',
    high: 'bg-tier-high text-white',
    mid: 'bg-tier-mid text-white',
    entry: 'bg-tier-entry text-foreground',
  };

  return (
    <div className={cn('relative py-4 my-4 bg-gradient-to-r border-l-4', tierColors[tier.color])}>
      <div className="container mx-auto px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={cn('px-3 py-1 rounded-full text-xs font-semibold', tierBadgeColors[tier.color])}>
            {tier.name}
          </span>
          <span className="text-sm text-muted-foreground">{tier.resolutionNote}</span>
        </div>
        <span className="text-xs text-muted-foreground hidden md:block">
          {tier.minPerformance}% - {tier.maxPerformance}% relative to RTX 4090
        </span>
      </div>
    </div>
  );
};

export const GPUComparisonTable = () => {
  const maxPrice = Math.max(...gpuData.map(g => g.msrp));
  
  const [filters, setFilters] = useState<GPUFilters>({
    search: '',
    brands: [],
    priceRange: [0, maxPrice],
    tiers: [],
  });

  const [selectedGPUs, setSelectedGPUs] = useState<GPU[]>([]);
  const [compareModalOpen, setCompareModalOpen] = useState(false);

  const toggleGPUSelection = (gpu: GPU) => {
    setSelectedGPUs(prev => {
      const isSelected = prev.some(g => g.id === gpu.id);
      if (isSelected) {
        return prev.filter(g => g.id !== gpu.id);
      }
      if (prev.length >= 4) return prev;
      return [...prev, gpu];
    });
  };

  const removeGPU = (gpuId: string) => {
    setSelectedGPUs(prev => prev.filter(g => g.id !== gpuId));
  };

  const clearSelection = () => {
    setSelectedGPUs([]);
  };

  // Filter GPUs based on current filters
  const filteredGPUs = useMemo(() => {
    return gpuData.filter((gpu) => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        if (!gpu.name.toLowerCase().includes(searchLower) &&
            !gpu.brand.toLowerCase().includes(searchLower) &&
            !gpu.architecture.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      // Brand filter
      if (filters.brands.length > 0 && !filters.brands.includes(gpu.brand)) {
        return false;
      }

      // Price filter
      if (gpu.msrp < filters.priceRange[0] || gpu.msrp > filters.priceRange[1]) {
        return false;
      }

      // Tier filter
      if (filters.tiers.length > 0) {
        const gpuTier = getTierForPerformance(gpu.performance);
        if (!gpuTier || !filters.tiers.includes(gpuTier.id)) {
          return false;
        }
      }

      return true;
    });
  }, [filters]);

  const sortedGPUs = sortGPUsByPerformance(filteredGPUs);
  
  // Group GPUs by performance brackets (every 5%)
  const performanceBrackets = new Map<number, GPU[]>();
  
  sortedGPUs.forEach(gpu => {
    const bracket = Math.floor(gpu.performance / 5) * 5;
    if (!performanceBrackets.has(bracket)) {
      performanceBrackets.set(bracket, []);
    }
    performanceBrackets.get(bracket)!.push(gpu);
  });

  const brackets = Array.from(performanceBrackets.entries()).sort((a, b) => b[0] - a[0]);

  let lastTier: string | null = null;

  const canSelectMore = selectedGPUs.length < 4;

  return (
    <section className="pb-24" aria-labelledby="comparison-heading">
      {/* Filter Bar */}
      <FilterBar filters={filters} onFiltersChange={setFilters} maxPrice={maxPrice} />

      {/* Results Count */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing <span className="font-medium text-foreground">{filteredGPUs.length}</span> of {gpuData.length} GPUs
          </p>
          {selectedGPUs.length > 0 && (
            <p className="text-sm text-primary font-medium">
              {selectedGPUs.length} GPU{selectedGPUs.length !== 1 ? 's' : ''} selected for comparison
            </p>
          )}
        </div>
      </div>

      {/* Table Header */}
      <div className="sticky top-[7.5rem] z-10 bg-background/95 backdrop-blur-sm border-b mb-4">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-3 gap-4 py-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-nvidia" />
              <span className="font-semibold text-sm">NVIDIA</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amd" />
              <span className="font-semibold text-sm">AMD</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-intel" />
              <span className="font-semibold text-sm">Intel</span>
            </div>
          </div>
        </div>
      </div>

      {/* GPU Rows by Performance */}
      <div className="container mx-auto px-4">
        {brackets.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground text-lg">No GPUs match your filters</p>
            <p className="text-sm text-muted-foreground mt-2">Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          brackets.map(([bracket, gpus]) => {
            const currentTier = performanceTiers.find(
              t => bracket >= t.minPerformance && bracket <= t.maxPerformance
            );
            
            const showTierDivider = currentTier && currentTier.id !== lastTier;
            if (currentTier) lastTier = currentTier.id;

            const nvidiaGPUs = gpus.filter(g => g.brand === 'nvidia');
            const amdGPUs = gpus.filter(g => g.brand === 'amd');
            const intelGPUs = gpus.filter(g => g.brand === 'intel');

            return (
              <div key={bracket}>
                {showTierDivider && currentTier && <TierDivider tier={currentTier} />}
                
                <div className="grid grid-cols-3 gap-4 mb-3">
                  <div className="space-y-2">
                    {nvidiaGPUs.map(gpu => (
                      <GPUCard
                        key={gpu.id}
                        gpu={gpu}
                        isSelected={selectedGPUs.some(g => g.id === gpu.id)}
                        onToggleSelect={toggleGPUSelection}
                        canSelect={canSelectMore}
                      />
                    ))}
                  </div>
                  <div className="space-y-2">
                    {amdGPUs.map(gpu => (
                      <GPUCard
                        key={gpu.id}
                        gpu={gpu}
                        isSelected={selectedGPUs.some(g => g.id === gpu.id)}
                        onToggleSelect={toggleGPUSelection}
                        canSelect={canSelectMore}
                      />
                    ))}
                  </div>
                  <div className="space-y-2">
                    {intelGPUs.map(gpu => (
                      <GPUCard
                        key={gpu.id}
                        gpu={gpu}
                        isSelected={selectedGPUs.some(g => g.id === gpu.id)}
                        onToggleSelect={toggleGPUSelection}
                        canSelect={canSelectMore}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Compare Bar */}
      <CompareBar
        selectedGPUs={selectedGPUs}
        onRemoveGPU={removeGPU}
        onCompare={() => setCompareModalOpen(true)}
        onClear={clearSelection}
      />

      {/* Compare Modal */}
      <CompareModal
        open={compareModalOpen}
        onOpenChange={setCompareModalOpen}
        gpus={selectedGPUs}
        onRemoveGPU={removeGPU}
      />
    </section>
  );
};
